/* eslint-disable react-hooks/exhaustive-deps */
import { ClickAwayListener } from '@material-ui/core'
import { useArticleIdFilter } from 'features/BillOfMaterials/BoMItemFilter/Components/ArticleIdFilter/useArticleIdFilter'
import { useResourceIdFilter } from 'features/BillOfMaterials/BoMItemFilter/Components/WorkingStepsFilter/useResourceIdFilter'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
  MoneyDto,
  PriceSummaryDto,
} from 'services/APIs/InternalAPI/internal-api.contracts'
import { Key } from 'utils/keyCodes'
import {
  TargetPriceTooltip,
  TargetPriceTooltipPayload,
} from '../../../Graph/Tooltips/TargetPriceTooltip'
import { MaterialsBarChart } from '../Graphs/MaterialsBarChart'
import { TotalsBarChart } from '../Graphs/TotalsBarChart'
import { WorkingStepsBarChart } from '../Graphs/WorkingStepsBarChart'

type Props = {
  priceSummaryKeys: string[]
  setPriceSummaryQuery: (description: string) => void
  isMaterialVisible: boolean
  isWorkingStepVisible: boolean
}

export type GraphData = {
  description: string
  category: string
  totalSalesPriceWorkingSteps: MoneyDto
  totalSalesPriceMaterials: MoneyDto
  workingStepPrices: PriceSummaryDto[]
  materialPrices: PriceSummaryDto[]
}

export const ProjectPriceSummaryGraph = (props: Props) => {
  const { handleChangeFilter: handleChangeResourceIdFilter } =
    useResourceIdFilter()
  const { handleChangeFilter: handleChangeArticleIdFilter } =
    useArticleIdFilter()

  const graphContainerRef = useRef<HTMLDivElement>()

  const mousePosition = useRef({ x: -200, y: -200 })

  const [tooltipData, setTooltipData] =
    useState<TargetPriceTooltipPayload>(undefined)

  useEffect(() => {
    const escapeHandler = (e: KeyboardEvent) => {
      if (e.key === Key.Escape) {
        setTooltipData(undefined)
      }
    }

    document.addEventListener('keydown', escapeHandler)

    return () => {
      document.removeEventListener('keydown', escapeHandler)
    }
  }, [])

  const handleMouseEnter = (e, data: PriceSummaryDto) => {
    mousePosition.current = { x: e.clientX, y: e.clientY }
    if (!data) {
      setTooltipData(undefined)
      return
    }

    // setTimeout(() => {
    setTooltipData({
      data: {
        id: data.materialId || data.workingStep?.resource?.id,
        description: data.description,
        primaryWorkingStep: data.workingStep?.primaryWorkingStep,
        secondaryWorkingStep: data.workingStep?.secondaryWorkingStep,
        resource: data.workingStep?.resource.name,
        matchDescription: data.workingStep?.resource?.matchType,
        productionTime: data.productionTime,
        salesPrice: data.totalSalesPrice,
        mainActivityCostPrice: data.totalCostPrice,
        total: data.totalSalesPrice,
        surchargeValue: data.surchargeValue,
        discountValue: data.discountValue,
        totalQuantity: data.totalQuantity,
        staggeredTotalQuantity: data.staggeredTotalQuantity,
      },
      tooltipDataType:
        data.isMaterial || data.isPurchasePartWithoutArticle
          ? 'Material'
          : 'WorkingStepSummary',
    })
    // }, 2)
  }

  const scrollAndFilter = (price: PriceSummaryDto) => {
    // document
    //   .getElementById(price.materialId || price.workingStep?.resource?.id)
    //   ?.scrollIntoView({
    //     behavior: 'smooth',
    //     block: 'center',
    //     inline: 'center',
    //   })

    if (price?.isWorkingStep) {
      handleChangeResourceIdFilter(price.workingStep?.resource?.id)
      handleChangeArticleIdFilter(undefined)
    } else if (price.isMaterial) {
      handleChangeArticleIdFilter(price.materialId)
      handleChangeResourceIdFilter(undefined)
    }

    props.setPriceSummaryQuery(price.description)
  }

  const GraphComponent = useMemo(
    // eslint-disable-next-line react/display-name
    () => (graphProps: { height?: number }) => {
      if (props.isMaterialVisible && props?.isWorkingStepVisible) {
        return (
          <TotalsBarChart
            {...props}
            handleMouseEnter={handleMouseEnter}
            scrollAndFilter={scrollAndFilter}
            containerHeight={graphProps.height}
          />
        )
      }

      if (props.isMaterialVisible) {
        return (
          <MaterialsBarChart
            {...props}
            handleMouseEnter={handleMouseEnter}
            scrollAndFilter={scrollAndFilter}
            containerHeight={graphProps.height}
          />
        )
      }

      if (props?.isWorkingStepVisible) {
        return (
          <WorkingStepsBarChart
            {...props}
            handleMouseEnter={handleMouseEnter}
            scrollAndFilter={scrollAndFilter}
            containerHeight={graphProps.height}
          />
        )
      }

      return null
    },
    [props.isMaterialVisible, props?.isWorkingStepVisible]
  )

  const boundingRect = graphContainerRef.current?.getBoundingClientRect()

  return (
    <>
      {/* {Boolean(tooltipData) && ( */}
      <TargetPriceTooltip
        position={mousePosition.current}
        payload={tooltipData}
        onClose={() => setTooltipData(undefined)}
        boundaries={
          boundingRect && {
            top: boundingRect.top,
            bottom: boundingRect.bottom,
            left: boundingRect.left,
            right: boundingRect.right,
          }
        }
        containerRef={graphContainerRef}
      />
      {/* )} */}
      <div ref={graphContainerRef}>
        <ClickAwayListener onClickAway={() => setTooltipData(undefined)}>
          <span>
            <GraphComponent height={204} />
          </span>
        </ClickAwayListener>
      </div>
    </>
  )
}
